/*
 * The four FleetControl calls the RBAC editor makes, written ONCE.
 *
 * WHY HERE, WHEN base-ui HOLDS NO TRANSPORT. It still holds none: every function
 * takes a `Transport` the caller already built, with the caller's own auth
 * interceptors on it. What lives here is the WIRE SHAPE — which RPC, what the
 * fields are called, how a `Result` error becomes a thrown message — and that is
 * exactly the thing three copies get subtly wrong in three ways.
 *
 * The alternative was a copy in fleet-ui, a second in ai-ui, and a third behind
 * app-ui's backend worker. That shape is already how the `profiles` tier came to
 * be enforced on one authoring RPC and not the other: two implementations of one
 * rule, and only one of them right. Connect and @vitesse/proto are PEER deps and
 * this is a subpath, so a UI that never imports it never bundles either.
 *
 * `orgId` is deliberately never sent. The server reads the caller's own org from
 * the verified token and accepts a different one only from a super-admin, so a
 * client-supplied org is ignored at best and a request to be refused at worst.
 */
import { createClient } from '@connectrpc/connect';
import { FleetControl } from '@vitesse/proto/vitesse/v1/fleet_control_pb.js';
import { getSession } from './session';
/*
 * THE LOCK SITS BESIDE THE CALL.
 *
 * Every caller already hides the button — but "the button is hidden" and "the
 * request cannot be sent" are different guarantees, and only the second survives
 * a caller that forgets. Both read the SAME annotation the server enforces
 * (`allowsRpc` resolves it from the method), so the lock and the refusal cannot
 * disagree, and the permission is never restated here.
 *
 * These two verbs sell DIFFERENT tiers — roles are `profiles`, the per-user
 * matrix is `per_user` — which is exactly why one flag cannot stand for both.
 */
function assertAllowed(method, name) {
    const session = getSession();
    // No session at all = a host that never initialised one (a test, a boot-time
    // path). Refusing there would break callers that were never gated in the first
    // place; the SERVER is still the authority either way.
    if (session && !session.allowsRpc(method)) {
        throw new Error(`you do not have permission to ${name}`);
    }
}
/** A `Result` envelope carries failure IN the reply rather than throwing, so a
 *  caller that only awaits the promise would treat a refusal as a success. */
function check(result, what) {
    if (result?.error)
        throw new Error(result.error.explanation || result.summary || `${what} failed`);
}
function toWire(m) {
    const out = {};
    // A PRESENT key is authored either way — true grants, false revokes — and an
    // absent key means "the role decides". Those are three answers, and collapsing
    // any two of them silently restores a permission an admin took away.
    for (const [key, granted] of Object.entries(m))
        out[key] = { granted };
    return out;
}
/** The inverse of toWire. Separate from the map inline at each call site for the
 *  same reason toWire is: the absent/true/false distinction is the whole contract,
 *  and it survives exactly as well as the number of places that implement it. */
function fromWire(m) {
    // Tolerates an ABSENT map, which is the ordinary case: a member with nothing
    // authored. protobuf-es materialises that as `{}`, but this decode also has to
    // survive a reply that simply omits the field — and the failure mode of not
    // tolerating it is a thrown TypeError that reads, from the editor, as the whole
    // roster being unavailable because one member has no grants yet.
    return Object.fromEntries(Object.entries(m ?? {}).map(([k, v]) => [k, v.granted]));
}
/**
 * Persist one member's authoring input.
 *
 * Sends `role` + `overrides` when a role is assigned and the bare matrix when it
 * is not — the server flattens. Sending the flattened product instead would make
 * a role a one-shot copy: editing "Operator" later would reach nobody, because
 * nothing would record that these members were authored from it.
 */
export async function setUserGrants(transport, userId, payload) {
    assertAllowed(FleetControl.method.setUserGrants, 'change what a member may do');
    const client = createClient(FleetControl, transport);
    const reply = payload.role
        ? await client.setUserGrants({ userId, role: payload.role, overrides: toWire(payload.overrides) })
        : await client.setUserGrants({ userId, grants: toWire(payload.grants) });
    check(reply.result, 'setUserGrants');
}
/** The org's roles, decoded. Read through the RPC rather than out of org metadata
 *  because the stored form is a compact permission→bitmask encoding, and a second
 *  implementation of that codec in the browser is drift the server never sees. */
export async function listOrgRoles(transport) {
    const client = createClient(FleetControl, transport);
    const reply = await client.listOrgRoles({});
    check(reply.result, 'listOrgRoles');
    // upstream/forked ride along so the editor can distinguish a role the org
    // invented from one it inherits — and, among the inherited, the ones it has
    // taken ownership of. Both are decided server-side; the client never infers
    // them, because "forked" is the absence of a stored override and only the
    // server can see that.
    return reply.roles.map((r) => ({
        name: r.name,
        grants: fromWire(r.grants),
        upstream: r.upstream,
        forked: r.forked,
    }));
}
/**
 * The org's ROSTER, joined with whatever RBAC each member has authored.
 *
 * THE EDITOR'S SOURCE OF TRUTH. The roster the grants are authored against is the
 * server's, not the SDK's, so the editor cannot list a person the write path
 * does not know about. There is no browser-side
 * membership listing to replace the Clerk SDK call this used to be — so without
 * this RPC the permission editor renders an empty list while every write path
 * behind it works perfectly.
 *
 * ONE CALL, NOT ONE PER MEMBER. The server joins the identity provider's roster
 * onto our authored matrices, so this returns whole rows; asking for the halves
 * separately would put a lookup per member in the browser against a store it
 * cannot filter.
 *
 * An ABSENT grant key means "the role decides" and a present-but-false one means
 * REVOKED. Those are different answers all the way from the fabric to this
 * decode, because collapsing them silently restores access an admin removed.
 */
export async function listOrgMembers(transport) {
    assertAllowed(FleetControl.method.listOrgMembers, "see this organization's members");
    const client = createClient(FleetControl, transport);
    const reply = await client.listOrgMembers({});
    check(reply.result, 'listOrgMembers');
    return reply.members.map((m) => ({
        userId: m.userId,
        name: m.name,
        email: m.email,
        role: m.role,
        // The AUTHORED half carries no avatar — a face belongs to the IdP, which owns
        // the person, not to fleet-control, which owns their permissions. `joinRoster`
        // fills it from the live roster.
        imageUrl: '',
        // The provider owns the join date too; joinRoster fills it from the roster.
        joinedAtMs: 0,
        grants: fromWire(m.grants),
        assignedRole: m.assignedRole,
        overrides: fromWire(m.overrides),
    }));
}
/** Replace the org's whole role set — SetOrgRoles is wholesale, so a role the
 *  editor deleted is simply absent from what we send. */
export async function setOrgRoles(transport, roles) {
    assertAllowed(FleetControl.method.setOrgRoles, 'author roles for this organization');
    const client = createClient(FleetControl, transport);
    const reply = await client.setOrgRoles({ roles: roles.map((r) => ({ name: r.name, grants: toWire(r.grants) })) });
    check(reply.result, 'setOrgRoles');
}
//# sourceMappingURL=rbac-rpc.js.map